import {
  Box,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useOktaAuth } from "@okta/okta-react";
import { useContext, useEffect, useState } from "react";
import { UserModuleContext } from "../../context/UserModuleContext";

const WSU_RED = "#A60F2D";

export const OktaGroups = () => {
  const [state, setState] = useState({ isLoaded: false, data: null, error: null });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { authState } = useOktaAuth();
  const { userModule } = useContext(UserModuleContext);

  const networkId = userModule.userData["Network ID"];

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(+e.target.value);
    setPage(0);
  };

  useEffect(() => {
    fetch(`${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/groups/okta`, {
      method: "POST",
      signal: AbortSignal.timeout(360000),
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: authState.accessToken.accessToken,
      },
      body: JSON.stringify({
        userId: networkId,
        callerId: authState.accessToken.claims.sub,
      }),
    })
      .then((res) => res.json())
      .then(
        (result) => setState({ data: result.Groups, isLoaded: true, error: null }),
        (error) => setState({ isLoaded: true, error, data: null })
      );
  }, []);

  if (!state.isLoaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
      </Box>
    );
  }

  if (state.error) {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Failed to load Okta groups
          </Typography>
          <Typography variant="body2">{state.error.message}</Typography>
        </Box>
      </TableContainer>
    );
  }

  if (!state.data || state.data.length === 0) {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Okta Groups
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>{networkId}</Typography>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" color="text.disabled" fontStyle="italic">
            No records found
          </Typography>
        </Box>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>

      {/* ── Section: Okta Groups ── */}
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
          Okta Groups
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>{networkId}</Typography>
      </Box>

      <Divider sx={{ mx: 3 }} />

      <TableContainer sx={{ maxHeight: 440 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: "#4D4D4D", color: "white", fontWeight: 700, fontSize: "0.8rem", letterSpacing: 0.5 }}>
                Group ID
              </TableCell>
              <TableCell sx={{ backgroundColor: "#4D4D4D", color: "white", fontWeight: 700, fontSize: "0.8rem", letterSpacing: 0.5 }}>
                Group Name
              </TableCell>
              <TableCell sx={{ backgroundColor: "#4D4D4D", color: "white", fontWeight: 700, fontSize: "0.8rem", letterSpacing: 0.5 }}>
                Group Description
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.data
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => (
                <TableRow key={row.ID} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                  <TableCell><Typography variant="body2">{row.ID}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{row.Profile.Name}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{row.Profile.Description}</Typography></TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={state.data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: "1px solid #e0e0e0",
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "0.8rem" },
        }}
      />
    </TableContainer>
  );
};
